import React, { useEffect, useState } from "react";
import * as api from "./csaApi";

/**
 * Off-checklist observations (design §9).
 *
 * A fixed framework has no home for "I noticed something that is not on the
 * checklist". Without one, auditors keep a paper pad for exactly that, and the
 * paper never goes away — which defeats the point of the whole module.
 *
 * These print in the report but never score. Letting them into the denominator
 * would make two sites incomparable: a site whose auditor noticed more would
 * look worse than one whose auditor noticed less.
 */
export default function CsaExtras({ auditId, readOnly, framework }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    let live = true;
    api
      .listExtras(auditId)
      .then((r) => live && setRows(r || []))
      .catch((e) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [auditId]);

  const save = async (draft) => {
    try {
      if (draft.id) {
        const r = await api.updateExtra(auditId, draft.id, draft);
        setRows((prev) => prev.map((x) => (x.id === r.id ? r : x)));
      } else {
        const r = await api.addExtra(auditId, draft);
        setRows((prev) => [...prev, r]);
      }
      setEditing(null);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  };

  const remove = async (id) => {
    try {
      await api.deleteExtra(auditId, id);
      setRows((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      setError(e.message);
    }
  };

  if (!rows) return <div className="cap-loading">Loading…</div>;

  return (
    <div className="cap-writeup">
      <div className="wu-head">
        <div>
          <h2>Other observations</h2>
          <p>
            Things worth reporting that the checklist does not ask about. These
            print in the report and never affect the score.
          </p>
        </div>
        <button
          type="button"
          className="cap-btn"
          disabled={readOnly}
          onClick={() => setEditing({ title: "", observation: "", recommendation: "", pillar_no: "" })}
        >
          Add an observation
        </button>
      </div>

      {error ? <p className="wu-error">{error}</p> : null}

      {rows.length === 0 && !editing ? (
        <div className="wu-empty">
          <h2>Nothing recorded</h2>
          <p>
            Most audits have none. Add one only when you saw something worth
            telling the client that no question covers.
          </p>
        </div>
      ) : null}

      {rows.map((x) => (
        <article className="wu-gap" key={x.id}>
          <header>
            <span className="wu-status s-extra">Off checklist</span>
            {x.pillar_no ? <span className="wu-where">P{x.pillar_no}</span> : null}
            <span className="wu-where" style={{ marginLeft: "auto" }}>
              <button type="button" className="small-btn" disabled={readOnly} onClick={() => setEditing(x)}>
                Edit
              </button>{" "}
              <button type="button" className="small-btn warn" disabled={readOnly} onClick={() => remove(x.id)}>
                Delete
              </button>
            </span>
          </header>
          <h3 className="wu-extra-title">{x.title}</h3>
          <p className="wu-statement">{x.observation}</p>
          {x.recommendation ? (
            <p className="wu-rec">
              <strong>Recommendation</strong>
              <br />
              {x.recommendation}
            </p>
          ) : null}
        </article>
      ))}

      {editing ? (
        <ExtraForm
          draft={editing}
          pillars={(framework && framework.pillars) || []}
          onCancel={() => setEditing(null)}
          onSave={save}
        />
      ) : null}
    </div>
  );
}

function ExtraForm({ draft, pillars, onCancel, onSave }) {
  const [form, setForm] = useState({
    id: draft.id,
    title: draft.title || "",
    observation: draft.observation || "",
    recommendation: draft.recommendation || "",
    pillar_no: draft.pillar_no || "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const ready = form.title.trim() && form.observation.trim();

  return (
    <>
      <div className="csa-pad-scrim" onClick={onCancel} />
      <div className="scope-dialog wide" role="dialog" aria-modal="true">
        <h3>{form.id ? "Edit observation" : "New observation"}</h3>

        <label htmlFor="x-title">Short title</label>
        <input id="x-title" type="text" value={form.title} onChange={set("title")} maxLength={200} />

        <label htmlFor="x-obs">What you saw</label>
        <textarea id="x-obs" rows={3} value={form.observation} onChange={set("observation")} />

        <label htmlFor="x-rec">Recommendation (optional)</label>
        <textarea id="x-rec" rows={2} value={form.recommendation} onChange={set("recommendation")} />

        <label htmlFor="x-pillar">Closest pillar (optional — for where it prints)</label>
        <select id="x-pillar" value={form.pillar_no} onChange={set("pillar_no")}>
          <option value="">Not tied to a pillar</option>
          {pillars.map((p) => (
            <option key={p.pillar_no} value={p.pillar_no}>
              P{p.pillar_no} · {p.pillar_name}
            </option>
          ))}
        </select>

        <div className="scope-actions">
          <button type="button" className="small-btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="cap-btn"
            disabled={!ready}
            onClick={() =>
              onSave({
                ...form,
                pillar_no: form.pillar_no === "" ? null : Number(form.pillar_no),
              })
            }
          >
            Save
          </button>
        </div>
      </div>
    </>
  );
}
